import React, { MouseEventHandler, PropsWithChildren, ReactNode, useCallback, useState } from "react";
import { Modal, ModalProps, message } from "@pionex-web-kit/components";
import { PaymentMethod } from "landings/V2/BuyCrypto/Buy/cards";
import { useTranslators } from "commonUse/Locale";
import csn from "classnames";
import { IconCheckMark16pxRegular, IconRightArrow12pxRegular } from "@pionex-web-kit/icons";
import { LinkPlaidWrap } from "landings/V2/CircleACH/plaid";
import { useAllowAddCard } from "landings/V2/BuyCrypto/hooks";
import { history } from "landings";
import { USD_DW_ROUTE_V2 } from "landings/V2/USDDW/menuRouter";
import DataReporter from "landings/Analytics/DataReporter";
import { useCallbackStatic } from "commonUse/tools";
import cs from "landings/V2/BuyCrypto/components/Form/index.m.less";
import { EventName } from "landings/Analytics/EventName";
import StripeACH from "../StripeACH";
import { useWhiteList } from "use/useWhiteList";
import { SafeDecimal } from "trade_utils_lib";
import { useACHActivity } from "landings/V2/CircleACH/utils";
import { useBalanceTradeByBaseQuote } from "commonUse/useBalance";

interface Props extends ModalProps {
    selectKey?: string;
    paymentMethods: PaymentMethod[];
    refresh: () => void;
    onChange?: (v: string | undefined) => void;
    amount?: string;
    coin?: string;
}

/**
 * 选择支付方式的弹窗
 */
const SelectPayMethodModal: React.FC<Props> = ({ selectKey, onChange, paymentMethods, refresh, amount, coin, ...resetProps }) => {
    const { $st } = useTranslators();

    const { allow: allowAddCard, processFunc: addCardProcess } = useAllowAddCard();

    const [extraContent, setExtraContent] = useState<ReactNode>();
    const clearExtraContent = useCallbackStatic(() => {
        setExtraContent(undefined);
    });

    const [stripeAchFlag, setStripeAchFlag] = useState<boolean>();
    const activity = useACHActivity("deposit");
    const [{ baseAvailable: usdAvailable }] = useBalanceTradeByBaseQuote("USD", "USDT"); // 原始的余额

    return (
        <>
            <Modal {...resetProps} maskClosable={false} keyboard={false} title={$st("buy_crypto_select_paymethod")} wSize="small" className="h-600px" closable footer={null}>
                {paymentMethods.map((item) => (
                    <>
                        {usdAvailable > 0.01 ? (
                            <div
                                className={csn(
                                    "flex justify-between items-center py-14px px-12px mt-8px bg-bg rounded-10px first:mt-0 cursor-pointer hover:bg-divider text-base font-m ",
                                    "dark:bg-divider dark:hover:bg-secondary-sub",
                                    selectKey === item.getKey() ? `${cs.selectChannel} border-solid border border-primary !bg-opacity-15 !bg-primary text-[#FF7028]` : "",
                                )}
                                onClick={(e) => {
                                    setExtraContent(item?.renderExtraContent?.(clearExtraContent));

                                    if (item?.disabled()) {
                                        return;
                                    }

                                    onChange?.(item.getKey());
                                    resetProps.onCancel?.(e);
                                }}
                            >
                                {item.renderItem()}
                                {selectKey === item.getKey() && <IconCheckMark16pxRegular size={16} />}
                            </div>
                        ) : (
                            <LinkItemWrapper
                                onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                                    message.error($st("not_enough_balance"))
                                    setExtraContent(item?.renderExtraContent?.(clearExtraContent));

                                    if (item?.disabled()) {
                                        return;
                                    }

                                    onChange?.(item.getKey());
                                    resetProps.onCancel?.(e);
                                }}
                            >
                                {item.renderItem()}
                            </LinkItemWrapper>
                        )}
                    </>
                ))}
                <div className="mt-20px text-base text-secondary">{$st("buy_crypto_link_new")}</div>
                {usdAvailable > 0.01 ? (
                    <LinkItemWrapper
                        onClick={(e: React.MouseEvent<HTMLElement>) => {
                            resetProps.onCancel?.(e);
                            if (!amount || !allowAddCard) {
                                addCardProcess?.();
                                return;
                            }
                            if (new SafeDecimal(amount).lessThan(2)) {
                                message.warning($st("buy_crypto_amount_least", { least: 2 }));
                                return;
                            }
                            setStripeAchFlag(true);
                        }}
                    >
                        <span>
                            {$st("nav_title_usd_deposit_ach")} <img className="h-[16px]" src={activity?.image} alt={"activity"} />
                        </span>
                    </LinkItemWrapper>
                ) : (
                    <div
                        className={csn(
                            "flex justify-between items-center py-14px px-12px mt-8px bg-bg rounded-10px first:mt-0 cursor-pointer hover:bg-divider text-base font-m ",
                            "dark:bg-divider dark:hover:bg-secondary-sub",
                            `${cs.selectChannel} border-solid border border-primary !bg-opacity-15 !bg-primary text-[#FF7028]`,
                        )}
                        onClick={(e: React.MouseEvent<HTMLElement>) => {
                            resetProps.onCancel?.(e);
                            if (!amount || !allowAddCard) {
                                addCardProcess?.();
                                return;
                            }
                            if (new SafeDecimal(amount).lessThan(2)) {
                                message.warning($st("buy_crypto_amount_least", { least: 2 }));
                                return;
                            }
                            setStripeAchFlag(true);
                        }}
                    >
                        <span>
                            {$st("nav_title_usd_deposit_ach")} <img className="h-[16px]" src={activity?.image} alt={"activity"} />
                        </span>
                        <IconCheckMark16pxRegular size={16} />
                    </div>
                )}
                <LinkItemWrapper
                    onClick={() => {
                        if (!allowAddCard) {
                            addCardProcess?.();
                            return;
                        }

                        DataReporter.report(EventName.buycrypto_wire_select);
                        Modal.confirm({
                            title: $st("buy_crypto_wire_title"),
                            content: $st("buy_crypto_wire_des"),
                            okText: $st("fbo_addCard_transfer_type_wire"),
                            onOk: () => {
                                history.push(USD_DW_ROUTE_V2.WIRE.DEPOSIT);
                            },
                        });
                    }}
                >
                    {$st("fbo_addCard_transfer_type_wire")}
                </LinkItemWrapper>
                <div className="mt-8px text-sm text-secondary">{$st("buy_crypto_link_more")}</div>
            </Modal>
            {extraContent}
            {stripeAchFlag !== undefined && (
                <StripeACH
                    modalVisible={stripeAchFlag}
                    onClose={() => {
                        setStripeAchFlag(false);
                    }}
                    amount={amount}
                />
            )}
        </>
    );
};

export default SelectPayMethodModal;

const LinkItemWrapper: React.FC<PropsWithChildren<{ onClick?: MouseEventHandler }>> = ({ children, onClick }) => {
    return (
        <div className="flex justify-between items-center py-14px px-12px mt-12px border border-solid border-divider rounded-10px cursor-pointer" onClick={onClick}>
            {children}
            <IconRightArrow12pxRegular size={12} className="text-secondary-sub" />
        </div>
    );
};

function LinkItemCollapse({ title, children }: { title: ReactNode; children: ReactNode }) {
    const [isCollapse, setIsCollapse] = useState(true);
    const onClick = useCallback(() => {
        setIsCollapse(!isCollapse);
    }, [isCollapse]);
    return (
        <div className="flex flex-col px-12px mt-12px border border-solid border-divider rounded-10px">
            <div className="flex py-14px justify-between items-center cursor-pointer" onClick={onClick}>
                {title}
                <IconRightArrow12pxRegular className={`text-secondary-sub ${isCollapse ? "rotate-90" : ""}`} size={12} />
            </div>
            {isCollapse && children}
        </div>
    );
}
